/**
 * CommunityAddOnUpsell — surfaces the v3.1 community add-on SKUs
 * (community_addon_single, community_addon_bundle) to Pulse, Surge,
 * and Spectrum members. Hidden from Signal (must upgrade first) and
 * Attuned (already has all 9 communities).
 *
 * Calls startAlacarteCheckout() — the SKU-to-Stripe-price-ID lookup
 * happens server-side via the alacarte_prices table. NO price IDs
 * are hardcoded here.
 */
import { useState } from "react";
import { Plus, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProfile } from "@/lib/profile/useProfile";
import { startAlacarteCheckout } from "@/lib/billing/alacarteCheckout";
import { toast } from "sonner";
import type { TierId } from "@/lib/profile/types";

const ELIGIBLE_TIERS: TierId[] = ["tier_pulse", "tier_surge", "tier_spectrum"];

type AddOnKey = "community_addon_single" | "community_addon_bundle";

interface AddOnDisplay {
  title: string;
  price: string;
  interval: string;
  description: string;
  features: readonly string[];
  icon: typeof Plus;
  featured?: boolean;
}

const ADDON_DISPLAY: Record<AddOnKey, AddOnDisplay> = {
  community_addon_single: {
    title: "Add One Community",
    price: "$9.99",
    interval: "/month",
    description: "Unlock access to one additional Wavlynq community.",
    features: [
      "Choose any sibling app",
      "Cancel anytime",
      "Stacks with your current tier",
    ],
    icon: Plus,
  },
  community_addon_bundle: {
    title: "Add Three Communities",
    price: "$24.99",
    interval: "/month",
    description: "Unlock three additional communities at a bundled rate.",
    features: [
      "Choose any three sibling apps",
      "Save $4.98/mo vs. buying singles",
      "Cancel anytime",
    ],
    icon: Sparkles,
    featured: true,
  },
};

export const CommunityAddOnUpsell = () => {
  const { profile } = useProfile();
  const [loading, setLoading] = useState<AddOnKey | null>(null);

  const tier = profile?.tier;
  const isEligible = !!tier && ELIGIBLE_TIERS.includes(tier);

  if (!isEligible) return null;

  const handleCheckout = async (featureKey: AddOnKey) => {
    setLoading(featureKey);
    try {
      const url = await startAlacarteCheckout({
        feature_key: featureKey,
        purchase_type: "monthly",
        success_url: `${window.location.origin}/account?addon=success`,
        cancel_url: `${window.location.origin}/account?addon=cancelled`,
      });
      if (url) window.location.href = url;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Couldn't start checkout. Please try again.",
      );
      setLoading(null);
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <header>
        <h2 className="text-lg font-semibold">Expand your community access</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Add more Wavlynq communities to your subscription without upgrading your tier.
        </p>
      </header>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {(Object.keys(ADDON_DISPLAY) as AddOnKey[]).map((key) => {
          const item = ADDON_DISPLAY[key];
          const Icon = item.icon;
          const featured = !!item.featured;
          const isLoading = loading === key;

          return (
            <article
              key={key}
              className={`rounded-2xl border p-5 flex flex-col bg-background/40 transition-colors ${
                featured
                  ? "border-foreground shadow-[0_0_0_1px_hsl(var(--foreground))]"
                  : "border-border"
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${
                    featured ? "bg-foreground text-background" : "bg-secondary text-foreground"
                  }`}
                  aria-hidden
                >
                  <Icon className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold">{item.title}</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">{item.description}</p>
                </div>
              </div>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-3xl font-bold tracking-tight">{item.price}</span>
                <span className="text-sm text-muted-foreground">{item.interval}</span>
              </div>

              <ul className="mt-4 space-y-1.5 text-xs flex-1">
                {item.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" aria-hidden />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleCheckout(key)}
                disabled={loading !== null}
                variant={featured ? "default" : "outline"}
                className="mt-5 w-full"
              >
                {isLoading ? "Opening checkout…" : "Add to my plan"}
              </Button>
            </article>
          );
        })}
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground">
        Want unlimited access to all 9 communities? Upgrade to Attuned for $99.99/mo.
      </p>
    </section>
  );
};

export default CommunityAddOnUpsell;
